<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { RouterView, useRoute } from 'vue-router';

import { UErrorView, useError } from '@dott/core';

import { useAppBadge } from '@/shared/composables/useAppBadge';
import { useAuth } from '@/shared/composables/useAuth';
import { favoritePath, useFavorites } from '@/shared/composables/useFavorites';
import {
  provideNotifications,
  useNotifications,
} from '@/shared/composables/useNotifications';
import { useRevalidateOnResume } from '@/shared/composables/useRevalidateOnResume';
import { useSwNavigationListener } from '@/shared/composables/useSwNavigationListener';
import { useTheme } from '@/shared/composables/useTheme';
import { useVersionCheck } from '@/shared/composables/useVersionCheck';

import type { NavigationMenuItem } from '@nuxt/ui';

const { error } = useError();
const hasError = computed(() => Boolean(error.value?.statusCode));
const isCriticalError = computed(() => error.value?.critical === true);

const route = useRoute();
const { currentUser, isGranted, logout } = useAuth();

// Instantiate the notifications state once at the layout root and share it with
// every descendant (sidebar badge, /inbox page). This is the sole provider, and
// it must live above the sidebar: anything rendered only when the sidebar is
// expanded would tear down the live subscription and badge on collapse.
const notifications = useNotifications();
provideNotifications(notifications);
const { unreadCount, loadedUnreadCount, badgeText } = notifications;

// PWA: keep the eternally-open window on the latest release, route
// service-worker notification clicks that reach uncontrolled windows, and mirror
// the inbox unread count onto the installed app's dock/taskbar icon.
useVersionCheck();
useSwNavigationListener();
useAppBadge(loadedUnreadCount);

const open = ref(false);
const hovered = ref(false);
const keyboardFocused = ref(false);
const userMenuOpen = ref(false);
useEventListener('pointerdown', () => {
  keyboardFocused.value = false;
});
watch(userMenuOpen, (value) => {
  if (!value) hovered.value = false;
});
const compact = computed(() => !hovered.value && !keyboardFocused.value && !userMenuOpen.value);

function onSidebarFocus(event: FocusEvent) {
  if ((event.target as HTMLElement).matches(':focus-visible')) {
    keyboardFocused.value = true;
  }
}

function onSidebarBlur(event: FocusEvent) {
  if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
    keyboardFocused.value = false;
  }
}
const { preference } = useTheme();

// Views whose component instance survives navigation into a detail page, so
// scroll position and local view state are restored on the way back
// (Linear-style) instead of remounting from scratch. Matched against the
// component `name` each page declares via defineOptions. The detail page — and
// everything else — is intentionally excluded so it always mounts fresh.
const CACHED_PAGES = ['IssuesPage', 'SprintsPage', 'RequestsPage', 'InboxPage'];

// Linear's placement: the user's own pins sit above the main navigation, and
// the section is not rendered at all while there are none. A view that stopped
// being visible to the user is already absent from the server's answer, and the
// resume revalidation is what drops it from a window left open for days.
const {
  favorites,
  isActive: isFavoriteActive,
  refetch: refetchFavorites,
} = useFavorites();

useRevalidateOnResume(() => refetchFavorites());

const favoriteItems = computed<NavigationMenuItem[]>(() =>
  favorites.value.flatMap((favorite) => {
    const to = favoritePath(favorite);

    return to
      ? [{
          label: favorite.savedView.name,
          icon: 'i-lucide-list-filter',
          to,
          active: isFavoriteActive(favorite),
          onSelect: () => {
            open.value = false;
          },
        }]
      : [];
  }));

const navigationItems = computed<NavigationMenuItem[]>(() => {
  const items: NavigationMenuItem[] = [];

  if (isGranted('dt:core:notifications:list')) {
    items.push({
      label: 'Inbox',
      icon: 'i-lucide-inbox',
      to: '/inbox',
      // NavigationMenuItem renders a trailing badge; omit it at zero so the nav
      // stays clean when there's nothing unread.
      badge: unreadCount.value > 0 ? badgeText.value : undefined,
      onSelect: () => {
        open.value = false;
      },
    });
  }

  if (isGranted('dt:core:issues:list')) {
    items.push({
      label: 'Issues',
      icon: 'i-lucide-square-kanban',
      to: '/issues',
      // The board lives at the team-scoped `/team/:teamKey/issues`, which
      // path-prefix matching against `/issues` doesn't cover — highlight by
      // route name instead.
      // `TeamIssuesView` is the same page under a saved view's path — without
      // it the sidebar loses its highlight the moment a view is opened.
      active: ['TeamIssues', 'TeamIssuesView', 'Issues', 'Issue'].includes(String(route.name)),
      onSelect: () => {
        open.value = false;
      },
    });
  }

  // The sprints view consumes both the issue list and the sprint list.
  if (isGranted('dt:core:issues:list') && isGranted('dt:core:sprints:list')) {
    items.push({
      label: 'Sprints',
      icon: 'i-lucide-iteration-cw',
      to: '/sprints',
      // Team-scoped like Issues — highlight by route name, not path prefix.
      active: ['TeamSprints', 'Sprints'].includes(String(route.name)),
      onSelect: () => {
        open.value = false;
      },
    });
  }

  if (isGranted('dt:core:requests:list')) {
    items.push({
      label: 'Requests',
      icon: 'i-lucide-mail-question',
      to: '/requests',
      // `/requests` only redirects to the organization-scoped
      // `/org/:orgKey/requests` (and its saved views under
      // `/org/:orgKey/views/:slug`), which path-prefix matching never reaches —
      // highlight by route name, like Issues.
      active: ['OrganizationRequests', 'RequestsView', 'Requests', 'Request'].includes(String(route.name)),
      onSelect: () => {
        open.value = false;
      },
    });
  }

  if (isGranted('dt:core:projects:list')) {
    items.push({
      label: 'Projects',
      icon: 'i-lucide-layers',
      to: '/projects',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  // After Projects, before the admin group: reading releases is everyone's,
  // including STAKEHOLDER, whose whole question is "did what you promised ship?"
  if (isGranted('dt:core:releases:list')) {
    items.push({
      label: 'Releases',
      icon: 'i-lucide-package',
      to: '/releases',
      // A release's own page is a separate route, not a child of the list, so
      // highlight by route name to stay lit there too.
      active: ['Releases', 'Release'].includes(String(route.name)),
      onSelect: () => {
        open.value = false;
      },
    });
  }

  // One level above epics, readable by the same people: a program page is what
  // a customer opens to see which step of their goal is next.
  if (isGranted('dt:core:programs:list')) {
    items.push({
      label: 'Programs',
      icon: 'i-lucide-goal',
      to: '/programs',
      active: ['Programs', 'Program'].includes(String(route.name)),
      onSelect: () => {
        open.value = false;
      },
    });
  }

  // Next to Releases and readable by the same people: an epic page is what a
  // customer opens to see how far along their feature is.
  if (isGranted('dt:core:epics:list')) {
    items.push({
      label: 'Epics',
      icon: 'i-lucide-milestone',
      to: '/epics',
      active: ['Epics', 'Epic'].includes(String(route.name)),
      onSelect: () => {
        open.value = false;
      },
    });
  }

  if (isGranted('dt:core:users:create')) {
    items.push({
      label: 'Users',
      icon: 'i-lucide-users',
      to: '/users',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  if (isGranted('dt:core:teams:update')) {
    items.push({
      label: 'Teams',
      icon: 'i-lucide-users',
      to: '/settings/teams',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  // Manage-gated (managers/admins): clients and plain users can already see
  // org labels through pickers and filters; the settings page is for editing.
  if (isGranted('dt:core:organizations:create')) {
    items.push({
      label: 'Organizations',
      icon: 'i-lucide-building-2',
      to: '/settings/organizations',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  // Manage-gated like the Teams/Users settings entries — every role can already
  // see labels through pickers and filters; the settings page is for editing.
  if (isGranted('dt:core:labels:create')) {
    items.push({
      label: 'Labels',
      icon: 'i-lucide-tag',
      to: '/settings/labels',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  // Read-gated, unlike the manage-gated settings entries above: this page lists
  // the caller's own views too, and each row's actions are gated individually.
  if (isGranted('dt:core:saved-views:list')) {
    items.push({
      label: 'Views',
      icon: 'i-lucide-list-filter',
      to: '/settings/views',
      onSelect: () => {
        open.value = false;
      },
    });
  }

  items.push({
    label: 'Help & documentation',
    icon: 'i-lucide-circle-help',
    to: '/help',
  });

  return items;
});

const navigationUi = {
  root: 'gap-0',
  list: 'flex flex-col gap-1',
  link: 'h-8 gap-2 text-[13px] font-normal rounded-md px-2 py-1.5 before:inset-0',
  linkLeadingIcon: 'shrink-0 size-4',
  linkBadge: 'h-4 min-w-4 justify-center rounded px-1 text-[10px]',
  linkLabel: 'truncate',
};

// The rail never switches the menu into Nuxt UI's `collapsed` mode: that swaps
// every item's component, so a link focused by Shift-Tab (which is what expands
// the rail) is unmounted and focus falls to <body>. The nodes stay put instead
// and only the label and badge are hidden while compact. The label stays in the
// accessibility tree, so an icon-only link still reads as "Issues". Tooltips
// are not needed: hovering the rail expands it.
const railNavigationUi = computed(() => ({
  ...navigationUi,
  linkLabel: compact.value ? 'truncate sr-only' : navigationUi.linkLabel,
  linkTrailing: compact.value ? 'hidden' : undefined,
}));

const userMenuItems = computed(() => [[{
  label: 'My profile',
  icon: 'i-lucide-user',
  to: '/me/edit',
}], [{
  label: 'Appearance',
  icon: 'i-lucide-sun-moon',
  children: (['light', 'dark', 'system'] as const).map((value) => ({
    label: value.charAt(0).toUpperCase() + value.slice(1),
    type: 'checkbox' as const,
    checked: preference.value === value,
    onSelect: () => { preference.value = value; },
  })),
}], [{
  label: 'Sign out',
  icon: 'i-lucide-log-out',
  onSelect: () => logout(),
}]]);
</script>

<template>
  <!-- Critical error: hide entire layout, show only error -->
  <template v-if="hasError && isCriticalError">
    <UErrorView />
  </template>

  <!-- Normal layout -->
  <template v-else>
    <div
      class="hidden w-12 shrink-0 lg:block"
      aria-hidden="true"
    />
    <UDashboardSidebar
      id="main"
      v-model:open="open"
      class="dott-sidebar bg-muted border-0"
      :class="{ 'is-expanded': !compact }"
      :ui="{ header: 'px-2 gap-2', body: 'px-2 py-2' }"
      @pointerenter="hovered = true"
      @pointerleave="hovered = false"
      @focusin="onSidebarFocus"
      @focusout="onSidebarBlur"
    >
      <template #header>
        <UDropdownMenu
          v-model:open="userMenuOpen"
          :items="userMenuItems"
          :ui="{ content: 'min-w-56' }"
          class="w-full min-w-0"
        >
          <UButton
            color="neutral"
            variant="ghost"
            class="w-full"
            data-testid="sidebar-user-button"
            :ui="{ base: 'h-8 gap-2 px-1.5 py-1', trailingIcon: 'ms-auto size-3.5 text-dimmed' }"
            :trailing-icon="compact ? undefined : 'i-lucide-chevron-down'"
          >
            <UAvatar
              :src="currentUser.avatar?.renditions?.[0]?.url"
              :alt="currentUser.fullName"
              size="2xs"
            />
            <!-- Always rendered: the compact rail only hides it visually, so
                 the button keeps the user's name as its label. -->
            <span
              class="truncate text-sm font-medium text-highlighted"
              :class="{ 'sr-only': compact }"
              data-testid="sidebar-user-name"
            >
              {{ currentUser.fullName }}
            </span>
          </UButton>
        </UDropdownMenu>
      </template>

      <template #default>
        <section
          v-if="favoriteItems.length > 0"
          class="flex flex-col"
          data-testid="sidebar-favorites"
        >
          <!-- Hidden, not removed, on the compact rail: it keeps its height,
               so expanding the rail never shifts the links below it. -->
          <p
            class="truncate px-2 pb-1 text-xs font-medium text-dimmed"
            :class="{ invisible: compact }"
          >
            Favorites
          </p>
          <UNavigationMenu
            :items="favoriteItems"
            :ui="railNavigationUi"
            color="neutral"
            orientation="vertical"
            aria-label="Favorites"
          />
          <USeparator class="my-2" />
        </section>

        <UNavigationMenu
          :items="navigationItems"
          :ui="railNavigationUi"
          color="neutral"
          orientation="vertical"
          aria-label="Main navigation"
          data-testid="sidebar-navigation"
        />
      </template>
    </UDashboardSidebar>

    <!-- min-w-0 lets the panel shrink to the free space next to the sidebar;
         without it wide content (e.g. the board) pushes the layout past the
         viewport instead of scrolling inside its own container. -->
    <div class="dott-workspace min-w-0 flex-1 min-h-0 flex flex-col bg-default">
      <!-- Non-critical error: show within layout -->
      <UErrorView v-if="hasError" />
      <RouterView
        v-else
        v-slot="{ Component }"
      >
        <KeepAlive :include="CACHED_PAGES">
          <component :is="Component" />
        </KeepAlive>
      </RouterView>
    </div>
  </template>
</template>

<style>
.dott-sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 40;
  width: 3rem;
  min-width: 3rem;
  transition: width 160ms ease;
}

.dott-sidebar.is-expanded {
  width: 14rem;
  box-shadow: 8px 0 24px rgb(0 0 0 / 12%);
  border-right: 1px solid var(--ui-border-muted);
}

.dott-workspace {
  margin: 8px 8px 8px 0;
  height: calc(100svh - 16px);
  overflow: hidden;
  border: 1px solid var(--ui-border-muted);
  border-radius: 10px;
}
@media (prefers-reduced-motion: reduce) {
  .dott-sidebar { transition: none; }
}
</style>
